import { useQueryClient } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import { useToast } from "@wayflyer/flyui";
import { usePostHog } from "posthog-js/react";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { PAYOUT_ACCOUNT_CONNECTOR_SLUGS } from "../../../components/constants";
import { composePath, PAGE_TYPE } from "../../../hooks/navigation-next/use-funding-application-navigation-next";
import { invalidateBankingSourceQueries, useSyncPayoutAccounts } from "../../../queries/bank-connect/queries";

import { MESSAGES } from "./message-definitions";

const DETECTED_BANK_ACCOUNT_TOAST_ID = "detected-bank-account";

export const useDetectedBankAccountToast = () => {
  const intl = useIntl();
  const toaster = useToast();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const { navigate } = useSpaNavigate("FundingApp");
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const syncPayoutAccounts = useSyncPayoutAccounts(customerDomain);

  return async (connectorSlug: string) => {
    if (!PAYOUT_ACCOUNT_CONNECTOR_SLUGS.includes(connectorSlug)) {
      return;
    }
    try {
      const { missingAccountPlatformName: platformName } = await syncPayoutAccounts.mutateAsync();
      if (!platformName) {
        return;
      }
      void invalidateBankingSourceQueries(queryClient, customerDomain);

      posthog?.capture("onboarding_detected_bank_account_viewed", { platform: platformName });
      toaster.info(intl.formatMessage(MESSAGES.detectedBankAccountToast, { platform: platformName }), {
        id: DETECTED_BANK_ACCOUNT_TOAST_ID,
        action: {
          content: intl.formatMessage(MESSAGES.detectedBankAccountToastAction),
          onAction: () => navigate(composePath(PAGE_TYPE.BANKING)),
          analyticsId: "detected-bank-account-toast-connect",
        },
      });
    } catch (e: unknown) {
      addError(e, { message: "connection-manager: detected bank account toast error" });
    }
  };
};
